import "./paginator.component.css";
import template from "./paginator.component.handlebars";

import Handlebars from "handlebars";
import "../../helpers/handlebars-helpers.js";
import { Observable, combineLatest } from "rxjs";
import { distinctUntilChanged, map, startWith, tap } from "rxjs/operators";
import { MakePaginationState, PaginationState } from "./pagination-state";
import { drop, take } from "lodash-es";
import $ from "jquery";
import { taggedMerge } from "src/js/helpers/taggedMerge";

const renderer = Handlebars.compile(template);

interface PageData<T> {
  pageNumber: number;
  items: T[];
}

interface PageChange<T> {
  kind: "PageChange";
  pageData: PageData<T>;
}

interface DataChange<T> {
  kind: "DataChange";
  pageData: PageData<T>;
}

type PaginatorEvent<T> = PageChange<T> | DataChange<T>;

export class Paginator {
  static paginate<T>(
    paginationContainers: HTMLElement[],
    pageSize$: Observable<number>,
    items$: Observable<T[]>
  ): Observable<PaginatorEvent<T>> {
    const pageEvent$ = this.#getSetPageEvent$(paginationContainers);

    const event: Observable<PaginatorEvent<T>> = taggedMerge({
      pageEvent: pageEvent$,
      items: items$,
    }).pipe(
      map(([type, data]) => {
        switch (type) {
          case "pageEvent":
            return {
              kind: "PageChange",
              pageData: { pageNumber: data, items: [] },
            };
          default:
            return {
              kind: "DataChange",
              pageData: { pageNumber: 1, items: [] },
            };
        }
      })
    );

    return combineLatest({
      items: items$,
      pageSize: pageSize$.pipe(distinctUntilChanged()),
      currentPageEvent: event,
    }).pipe(
      tap(({ items, pageSize, currentPageEvent: currentPageEvent }) => {
        const state = MakePaginationState(
          items.length,
          pageSize,
          currentPageEvent.pageData.pageNumber
        );
        paginationContainers.forEach((container) => {
          this.#renderPaginator(renderer, state, container);
        });
      }),
      map(({ items, pageSize, currentPageEvent: currentPageEvent }) => {
        const tail = drop(
          items,
          (currentPageEvent.pageData.pageNumber - 1) * pageSize
        );
        const page = take(tail, pageSize);

        let newEvent = structuredClone(currentPageEvent);
        newEvent.pageData.items = page;
        return newEvent;
      })
    );
  }

  static #getSetPageEvent$(
    paginationContainers: HTMLElement[]
  ): Observable<number> {
    return new Observable((observer) => {
      $(paginationContainers).on("click", ".paginator-button", (ev) => {
        ev.preventDefault();

        const pageNumber = parseInt(ev.target.dataset.val);

        observer.next(pageNumber);
      });
    });
  }

  static #renderPaginator(
    renderFunction: HandlebarsTemplateDelegate<PaginationState>,
    paginatorState: PaginationState,
    containerElement: HTMLElement
  ) {
    const html = renderFunction(paginatorState, {
      allowProtoPropertiesByDefault: true,
    });

    containerElement.innerHTML = html;
  }
}
